import {
	Paper,
	SortDirection,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { visuallyHidden } from "@mui/utils"
import React, { MouseEvent } from "react"
import { useSelector } from "react-redux"

import { RootState } from "../../../store"
import { BanksListState, BankState } from "../../../store/actions/bank"
import NoData from "../../error/nodata"
import UserRow from "./row"

interface IEnhancedBankTable {
	sortCriteria: [{ field: string; order: string }]
	setSortCriteria: (value: { field: string; order: string }[]) => void
}

interface IEnhancedTableHeadProps {
	sortCriteria: [{ field: string; order: string }]
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		property: keyof BankState
	) => void
}

interface HeadCell {
	id: keyof BankState
	label: string
	numeric: boolean
	disablePadding: boolean
}

const TableCellStyled = styled(TableCell)(() => ({
	paddingLeft: "8px",
}))

const TableHeadStyled = styled(TableHead)(() => ({
	backgroundColor: "#E9EDF0",
}))

const headCells: HeadCell[] = [
	{ id: "bankName", numeric: false, disablePadding: false, label: "Nome" },
	{
		id: "timeToExpiry",
		numeric: true,
		disablePadding: false,
		label: "Scadenza",
	},
	{
		id: "primaryColor",
		numeric: false,
		disablePadding: false,
		label: "Colore primario",
	},
	{
		id: "secondaryColor",
		numeric: false,
		disablePadding: false,
		label: "Colore secondario",
	},
	{
		id: "autoAccept",
		numeric: false,
		disablePadding: false,
		label: "Auto accettazione",
	},
	{
		id: "checkIban",
		numeric: false,
		disablePadding: false,
		label: "Check iban",
	},
	{ id: "whatsapp", numeric: false, disablePadding: false, label: "Whatsapp" },
]

function EnhancedTableHead(props: IEnhancedTableHeadProps) {
	const { sortCriteria, onRequestSort } = props
	const createSortHandler =
		(property: keyof BankState) => (event: MouseEvent<unknown>) => {
			onRequestSort(event, property)
		}

	return (
		<TableHeadStyled>
			<TableRow>
				<TableCellStyled />
				{headCells.map((headCell) => {
					const cellSorted:
						| { field: string; order: SortDirection }
						| undefined = sortCriteria.find(
						(criteria: { field: string; order: SortDirection }) =>
							criteria.field === headCell.id
					)
					return (
						<TableCellStyled
							key={headCell.id}
							align={headCell.numeric ? "right" : "left"}
							padding={headCell.disablePadding ? "none" : "normal"}
							sortDirection={!!cellSorted ? cellSorted.order : undefined}
						>
							<TableSortLabel
								active={!!cellSorted}
								direction={!!cellSorted ? cellSorted.order : "asc"}
								onClick={createSortHandler(headCell.id)}
							>
								{headCell.label}{" "}
								{!!cellSorted ? (
									<span style={visuallyHidden}>
										{" "}
										{cellSorted.order === "desc"
											? "sorted descending"
											: "sorted ascending"}{" "}
									</span>
								) : null}
							</TableSortLabel>
						</TableCellStyled>
					)
				})}
				<TableCellStyled />
			</TableRow>
		</TableHeadStyled>
	)
}

const BankEnhancedTable = ({
	sortCriteria,
	setSortCriteria,
}: IEnhancedBankTable) => {
	const bankState: BanksListState = useSelector(
		(state: RootState) => state.banksPaginated
	)

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof BankState
	) => {
		const cellSorted: { field: string; order: SortDirection } | undefined =
			sortCriteria.find(
				(criteria: { field: string; order: SortDirection }) =>
					criteria.field === property
			)
		const isAsc = !!cellSorted ? cellSorted.order : false
		setSortCriteria([{ field: property, order: isAsc ? "desc" : "asc" }])
	}

	return (
		<TableContainer component={Paper} sx={{ marginTop: "16px" }}>
			<Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
				<EnhancedTableHead
					sortCriteria={sortCriteria}
					onRequestSort={handleRequestSort}
				/>
				<TableBody>
					{bankState?.list?.page?.content &&
						bankState.list.page.content.map((row: BankState) => (
							<UserRow row={row} />
						))}

					{bankState?.list?.page?.content?.length === 0 && (
						<TableRow>
							<TableCellStyled
								colSpan={9}
								style={{ textAlign: "center" }}
								component="td"
								scope="row"
								padding="none"
							>
								<NoData />
							</TableCellStyled>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	)
}
export default BankEnhancedTable
